import ConfirmationNumberIcon from '@offline-mui/icons-material/ConfirmationNumber'
import EditOutlinedIcon from '@offline-mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@offline-mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@offline-mui/material'
import type { ReactNode } from 'react'
import type {
  BookingPassenger,
  BookingPayment,
  InventoryBooking,
  PassengerDetails,
  PaxFareSnapshot,
} from '../api/inventory'
import { formatMoney, formatTicketBy } from '../utils/flightFormat'
import CopyJsonButton from './CopyJsonButton'

type BookingResultCardProps = {
  booking: InventoryBooking
  defaultExpanded?: boolean
  onEdit?: (booking: InventoryBooking) => void
}

function Field({ label, value }: { label: string; value: unknown }) {
  const text =
    value == null || value === ''
      ? '—'
      : typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : String(value)
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{text}</Typography>
    </Box>
  )
}

function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {action}
      </Stack>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  )
}

function passengerName(details: PassengerDetails | null | undefined): string {
  if (!details) return 'Passenger'
  return [details.prefix, details.firstName, details.middleName, details.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Passenger'
}

function bookingTotal(booking: InventoryBooking) {
  const fares = booking.pax_fare || []
  const amount = fares.reduce((sum, fare) => sum + Number(fare.totalAmount?.amount || 0), 0)
  const currency = fares[0]?.totalAmount?.currency || 'CAD'
  if (!fares.length) return null
  return { amount, currency }
}

function bookingStatus(booking: InventoryBooking): string {
  const statuses = (booking.passengers || [])
    .map((pax) => pax.pax_booking_status)
    .filter(Boolean)
  return statuses[0] || 'Booked'
}

function cardHint(value: string | null | undefined): string {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return '—'
  return `•••• ${digits.slice(-4)}`
}

function contactValues(
  items: Array<string | { email?: string; number?: string }> | undefined,
  key: 'email' | 'number',
): string[] {
  return (items || [])
    .map((item) => (typeof item === 'string' ? item : item[key] || ''))
    .filter(Boolean)
}

function FareRow({ fare }: { fare: PaxFareSnapshot }) {
  const qty = Number(fare.passengerDetails?.quantity ?? fare.quantity ?? 1)
  const label = [
    fare.passengerDetails?.code ||
      fare.passengerDetails?.type ||
      fare.passengerTypeCode ||
      fare.passengerType,
    qty > 1 ? `×${qty}` : null,
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', py: 0.5 }}>
      <Typography>{label || 'Fare'}</Typography>
      <Typography sx={{ fontWeight: 700 }}>{formatMoney(fare.totalAmount)}</Typography>
    </Stack>
  )
}

function PassengerBlock({ passenger }: { passenger: BookingPassenger }) {
  const details = passenger.pax_details
  const emails = contactValues(details?.contact?.emails, 'email')
  const phones = contactValues(details?.contact?.phones, 'number')

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography sx={{ fontWeight: 800 }}>{passengerName(details)}</Typography>
        <Chip size="small" label={passenger.pax_booking_status || 'Booked'} />
      </Stack>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field
            label="Type"
            value={details?.paxType || details?.passengerType || details?.passengerTypeCode}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Gender" value={details?.gender} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Date of birth" value={formatTicketBy(details?.dateOfBirth)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Nationality" value={details?.nationality} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="PNR" value={passenger.airline_record_locator} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="E-ticket number" value={passenger.ticket_number} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Field label="Email" value={emails.join(', ')} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Field label="Phone" value={phones.join(', ')} />
        </Grid>
      </Grid>
    </Box>
  )
}

function PaymentBlock({
  payment,
  passenger,
}: {
  payment: BookingPayment
  passenger?: string
}) {
  const details = payment.payment_details
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Type" value={details?.type || details?.paymentType} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Amount" value={formatMoney(details?.totalAmount)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Card" value={cardHint(details?.paymentDetails)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Field label="Passenger" value={passenger} />
        </Grid>
      </Grid>
    </Box>
  )
}

export default function BookingResultCard({
  booking,
  defaultExpanded = false,
  onEdit,
}: BookingResultCardProps) {
  const passengers = booking.passengers || []
  const payments = passengers.flatMap((pax) =>
    (pax.payments || []).map((payment) => ({
      payment,
      passenger: passengerName(pax.pax_details),
    })),
  )
  const total = bookingTotal(booking)
  const names = passengers.map((pax) => passengerName(pax.pax_details)).join(', ')

  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        '&:before': { display: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1.25,
            alignItems: 'center',
            gap: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ flex: 1, alignItems: { md: 'center' }, minWidth: 0 }}
        >
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Box
              sx={{
                minWidth: 40,
                height: 28,
                px: 1,
                borderRadius: 0.5,
                bgcolor: 'primary.dark',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {booking.itinerary?.plating_carrier || 'PNR'}
            </Box>
            <ConfirmationNumberIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ fontWeight: 800 }}>{booking.booking_id}</Typography>
            <Chip size="small" label={bookingStatus(booking)} />
          </Stack>

          <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
            {names || 'No passengers'}
          </Typography>

          <Typography sx={{ fontWeight: 700, flexShrink: 0 }}>
            {booking.itinerary_id ? `Block #${booking.itinerary_id}` : 'Orphan'}
          </Typography>

          <Typography sx={{ fontWeight: 800, flexShrink: 0 }}>
            {formatMoney(total)}
          </Typography>

          <Typography sx={{ fontWeight: 600, flexShrink: 0 }}>
            {formatTicketBy(booking.created_at)}
          </Typography>

          <Stack
            direction="row"
            spacing={0.25}
            sx={{ alignItems: 'center', flexShrink: 0 }}
            onClick={(event) => event.stopPropagation()}
            onFocus={(event) => event.stopPropagation()}
          >
            {onEdit ? (
              <Tooltip title="Edit PNR and e-ticket">
                <IconButton
                  size="small"
                  aria-label="Edit PNR and e-ticket"
                  onClick={() => onEdit(booking)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <CopyJsonButton value={booking} label="Copy booking JSON" />
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Stack spacing={1.5}>
          <Section title="Booking">
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Field label="Booking ID" value={booking.booking_id} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Field label="Block" value={booking.itinerary_id ?? 'Orphan'} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Field label="Fare type" value={booking.itinerary?.fare_type} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Field label="Created" value={formatTicketBy(booking.created_at)} />
              </Grid>
            </Grid>
            {booking.pax_fare?.length ? (
              <>
                <Divider sx={{ my: 1.5 }} />
                {booking.pax_fare.map((fare, index) => (
                  <FareRow key={`${fare.passengerTypeCode || 'fare'}-${index}`} fare={fare} />
                ))}
                <Stack direction="row" sx={{ justifyContent: 'space-between', pt: 0.5 }}>
                  <Typography sx={{ fontWeight: 800 }}>Total</Typography>
                  <Typography sx={{ fontWeight: 800 }}>{formatMoney(total)}</Typography>
                </Stack>
              </>
            ) : null}
          </Section>

          <Section
            title="Passengers"
            action={
              onEdit ? (
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => onEdit(booking)}
                >
                  Edit PNR / e-ticket
                </Button>
              ) : null
            }
          >
            {passengers.length ? (
              <Stack spacing={1.25}>
                {passengers.map((passenger) => (
                  <PassengerBlock key={passenger.id} passenger={passenger} />
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No passenger records.</Typography>
            )}
          </Section>

          <Section title="Payments">
            {payments.length ? (
              <Stack spacing={1.25}>
                {payments.map(({ payment, passenger }) => (
                  <PaymentBlock
                    key={payment.id}
                    payment={payment}
                    passenger={passenger}
                  />
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No payment records.</Typography>
            )}
          </Section>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
